import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency, CURRENCIES } from '../../../hooks/useCurrency';

const LANGUAGES = [
  { code: 'en', label: 'EN', full: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'FR', full: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'AR', full: 'العربية', flag: '🇸🇦' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const { currency, setCurrency } = useCurrency();

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) {
        setCurrencyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchLang = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    setLangOpen(false);
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-lg border-b border-white/10' : 'bg-transparent'
      }`}
      role="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <img src="https://4kdreamers.com/logo.webp" alt="Digicode Logo" className="h-8 sm:h-10 w-auto object-contain" width={250} height={100} />
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-medium text-white/80">
            <a href="#pricing" className="hover:text-white transition-colors">{t('nav.pricing')}</a>
            <a href="#avantages" className="hover:text-white transition-colors">{t('nav.features')}</a>
            <a href="#support" className="hover:text-white transition-colors">{t('nav.support')}</a>
          </div>

          {/* Right side: Currency + Language + CTA */}
          <div className="flex items-center gap-2 sm:gap-3">

            {/* Currency Switcher */}
            <div className="relative" ref={currencyRef}>
              <button
                onClick={() => { setCurrencyOpen(!currencyOpen); setLangOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/20 hover:border-white/40 text-white/80 hover:text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap bg-white/5"
              >
                <span className="font-bold">{currency.symbol}</span>
                <span className="hidden sm:inline text-xs">{currency.code}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${currencyOpen ? 'rotate-180' : ''}`} />
              </button>

              {currencyOpen && (
                <div className="absolute top-full mt-2 right-0 bg-gray-900/95 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden min-w-[140px] shadow-2xl z-50">
                  {CURRENCIES.map(c => (
                    <button
                      key={c.code}
                      onClick={() => { setCurrency(c); setCurrencyOpen(false); setMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                        currency.code === c.code
                          ? 'bg-primary/20 text-primary'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <span className="text-base">{c.flag}</span>
                      <span className="font-bold">{c.symbol}</span>
                      <span className="font-medium">{c.code}</span>
                      {currency.code === c.code && <i className="ri-check-line text-primary ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => { setLangOpen(!langOpen); setCurrencyOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/20 hover:border-white/40 text-white/80 hover:text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap bg-white/5"
              >
                <span>{currentLang.flag}</span>
                <span className="hidden sm:inline">{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
              </button>

              {langOpen && (
                <div className="absolute top-full mt-2 right-0 bg-gray-900/95 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden min-w-[140px] shadow-2xl z-50">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => switchLang(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                        i18n.language === lang.code
                          ? 'bg-primary/20 text-primary'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="font-medium">{lang.full}</span>
                      {i18n.language === lang.code && <i className="ri-check-line text-primary ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://wa.me/12057092376"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex bg-gradient-primary text-white px-4 md:px-6 py-2 rounded-lg font-semibold text-sm hover:opacity-90 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav.contact')}
            </a>

            {/* Mobile hamburger */}
            <button className="lg:hidden text-white p-2 cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-3">
            <a href="#pricing" className="block text-white/80 hover:text-white py-2 text-sm" onClick={() => setMenuOpen(false)}>{t('nav.pricing')}</a>
            <a href="#avantages" className="block text-white/80 hover:text-white py-2 text-sm" onClick={() => setMenuOpen(false)}>{t('nav.features')}</a>
            <a href="#support" className="block text-white/80 hover:text-white py-2 text-sm" onClick={() => setMenuOpen(false)}>{t('nav.support')}</a>

            {/* Mobile currency buttons */}
            <div className="flex gap-2 pt-1">
              {CURRENCIES.map(c => (
                <button
                  key={c.code}
                  onClick={() => { setCurrency(c); setMenuOpen(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border cursor-pointer transition-all ${
                    currency.code === c.code ? 'border-primary bg-primary/20 text-primary' : 'border-white/20 text-white/70 hover:text-white hover:border-white/40'
                  }`}
                >
                  <span>{c.flag}</span><span>{c.symbol} {c.code}</span>
                </button>
              ))}
            </div>

            {/* Mobile language buttons */}
            <div className="flex gap-2 pt-1">
              {LANGUAGES.map(lang => (
                <button key={lang.code} onClick={() => switchLang(lang.code)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer transition-all ${
                    i18n.language === lang.code ? 'border-primary bg-primary/20 text-primary' : 'border-white/20 text-white/70 hover:text-white hover:border-white/40'
                  }`}>
                  <span>{lang.flag}</span><span>{lang.label}</span>
                </button>
              ))}
            </div>
            <a href="https://wa.me/12057092376" target="_blank" rel="noopener noreferrer"
              className="block bg-gradient-primary text-white px-4 py-2 rounded-lg font-semibold text-sm text-center cursor-pointer">
              {t('nav.contact')}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
